import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Clock, Crown, Ellipsis, Send, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../SubscriptionProvider';
import TransferOwnershipDialog from './TransferOwnershipDialog';
import DeleteAIModal from './ai/DeleteAIModal';
import { httpClient } from '@/Config/api/httpClient.config';
import { QueryKey } from '@/Config/api/queryKey';
import { Avatar, AvatarFallback } from '@/Domain/components/Shared/Avatar/Avatar';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/Domain/components/Shared/Card/Card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/Domain/components/Shared/DropdownMenu/DropdownMenu';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import type { AIAssistantsData } from '@/Domain/components/TopBar/types';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { useGetSubscription } from '@/Domain/hooks/Subscription/useGetSubscription.hook';

enum Role {
  ADMIN = 'admin',
  EDITOR = 'editor',
  SUPPORT = 'support',
  VIEWER = 'viewer',
  OWNER = 'owner',
}

interface Invitation {
  email: string;
  role: Role;
  id: string;
  invited_at: string | null;
}

interface User {
  email: string;
  role: Role;
  id: string;
  // Backend-computed (can_orguser_update_role): may the requesting user PATCH/DELETE this row.
  can_modify: boolean;
  // Backend-computed: true only when the requesting user owns this agent.
  can_transfer_ownership: boolean;
  invited_at: string | null;
  joined_at: string | null;
}

const getInitials = (email: string): string => email.substring(0, 2).toUpperCase();

const formatDate = (value: string | null | undefined): string | null => {
  if (!value) return null;
  return format(parseISO(value), 'd MMM yyyy');
};

type MemberItemProps = {
  email: string;
  role: Role;
  invitedAt: string | null;
  joinedAt: string | null;
  isPending?: boolean;
  isOwner?: boolean;
  canEditRole?: boolean;
  isDeleting?: boolean;
  onRoleChange?: (role: Role) => void;
  onDelete?: () => void;
  onTransferOwnership?: () => void;
};

const MemberItem = ({
  email,
  role,
  invitedAt,
  joinedAt,
  isPending,
  isOwner,
  canEditRole,
  isDeleting,
  onRoleChange,
  onDelete,
  onTransferOwnership,
}: MemberItemProps) => {
  const { t } = useTranslation(['users', 'common']);
  const invitedAtLabel = formatDate(invitedAt);
  const joinedAtLabel = formatDate(joinedAt);

  return (
    <div className="flex items-center justify-between gap-2 p-2.5 px-4 border rounded-xl bg-card">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Avatar className="size-8 rounded-md bg-muted text-xs">
          <AvatarFallback className="text-xs rounded-md font-medium text-muted-foreground">
            {getInitials(email)}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground truncate">{email}</span>
            {isPending && (
              <Badge
                variant="secondary"
                size="sm"
                className="gap-1.5 text-xs font-medium bg-muted text-foreground-subtle"
              >
                <Clock className="w-3 h-3" />
                {t('pending_invite')}
              </Badge>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-foreground-subtle text-xs font-medium">
              {t(`role.${role}`, { ns: 'common' })}
            </span>
            {invitedAtLabel && (
              <>
                <span className="text-foreground-subtle text-xs">·</span>
                <span className="text-foreground-subtle text-xs font-medium">
                  {t('invited_on', { date: invitedAtLabel })}
                </span>
              </>
            )}
            {joinedAtLabel && (
              <>
                <span className="text-foreground-subtle text-xs">·</span>
                <span className="text-foreground-subtle text-xs font-medium">
                  {t('joined_on', { date: joinedAtLabel })}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {canEditRole && onRoleChange && (
          <Select value={role} onValueChange={(value) => onRoleChange(value as Role)}>
            <SelectTrigger size="sm" className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={Role.ADMIN}>
                {t(`role.${Role.ADMIN}`, { ns: 'common' })}
              </SelectItem>
              <SelectItem value={Role.EDITOR}>
                {t(`role.${Role.EDITOR}`, { ns: 'common' })}
              </SelectItem>
              <SelectItem value={Role.SUPPORT}>
                {t(`role.${Role.SUPPORT}`, { ns: 'common' })}
              </SelectItem>
              <SelectItem value={Role.VIEWER}>
                {t(`role.${Role.VIEWER}`, { ns: 'common' })}
              </SelectItem>
            </SelectContent>
          </Select>
        )}

        {(onTransferOwnership || (!isOwner && onDelete)) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="size-8 shrink-0 p-0 text-muted-foreground hover:text-foreground"
                aria-label={t('users:member_actions.open_menu')}
                data-testid={`member-actions-trigger-${email}`}
              >
                {isDeleting ? <Spinner className="size-4" /> : <Ellipsis className="size-4" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {onTransferOwnership && (
                <DropdownMenuItem
                  onClick={onTransferOwnership}
                  aria-label={t('users:transfer_ownership.trigger')}
                  data-testid={`transfer-ownership-trigger-${email}`}
                >
                  <Crown className="mr-2 size-4" />
                  {t('users:transfer_ownership.trigger')}
                </DropdownMenuItem>
              )}

              {!isOwner && onDelete && (
                <DropdownMenuItem
                  onClick={onDelete}
                  disabled={isDeleting}
                  className="text-destructive focus:text-destructive"
                  aria-label={t('users:remove')}
                  data-testid={`remove-member-${email}`}
                >
                  <Trash2 className="mr-2 size-4" />
                  {t('users:remove')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};

const InvitationItem = ({ invitation }: { invitation: Invitation }) => {
  const queryClient = useQueryClient();
  const deleteInviteMutation = useMutation<unknown, unknown, string>({
    mutationFn: async (id) => await httpClient.delete(`/v1/api/scenario_users/invites/${id}`),
    onSuccess: (_, id) => {
      queryClient.setQueryData<Invitation[]>(['invitations'], (oldData = []) =>
        oldData.filter((inv) => inv.id !== id),
      );
    },
  });

  return (
    <MemberItem
      email={invitation.email}
      role={invitation.role}
      invitedAt={invitation.invited_at}
      joinedAt={null}
      isPending
      isDeleting={deleteInviteMutation.isPending}
      onDelete={() => deleteInviteMutation.mutate(invitation.id)}
    />
  );
};

const UserItem = ({ user }: { user: User }) => {
  const queryClient = useQueryClient();
  const { isActivated, isFetched: isSubscriptionFetched } = useSubscription();
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  const deleteUserMutation = useMutation<unknown, unknown, string>({
    mutationFn: async (id) => await httpClient.delete(`/v1/api/scenario_users/${id}`),
    onSuccess: (_, id) => {
      queryClient.setQueryData<User[]>(['users'], (users = []) => users.filter((u) => u.id !== id));
    },
  });

  const updateUserMutation = useMutation<User, unknown, Omit<User, 'email'>>({
    mutationFn: async ({ role }) =>
      await httpClient
        .patch(`/v1/api/scenario_users/${user.id}`, { role })
        .then(({ data }) => data),
    onSuccess: (data) => {
      queryClient.setQueryData<User[]>(['users'], (users = []) =>
        users.map((u) => (u.id === data.id ? data : u)),
      );
    },
  });

  // `user.can_modify` is computed by the backend so the UI doesn't have to
  // mirror the rules — the same predicate (`can_orguser_update_role`) gates
  // both PATCH/DELETE on /v1/api/scenario_users/{user_id} and the affordances
  // here.
  const isOwner = user.role === Role.OWNER;
  const canEditRole = (!isSubscriptionFetched || isActivated('user_roles')) && user.can_modify;

  return (
    <>
      <MemberItem
        email={user.email}
        role={user.role}
        invitedAt={user.invited_at}
        joinedAt={user.joined_at}
        isOwner={isOwner}
        canEditRole={canEditRole}
        isDeleting={deleteUserMutation.isPending}
        onRoleChange={(role) => updateUserMutation.mutate({ role, id: user.id })}
        onDelete={user.can_modify ? () => deleteUserMutation.mutate(user.id) : undefined}
        onTransferOwnership={
          user.can_transfer_ownership ? () => setIsTransferModalOpen(true) : undefined
        }
      />
      {user.can_transfer_ownership && (
        <TransferOwnershipDialog
          open={isTransferModalOpen}
          onOpenChange={setIsTransferModalOpen}
          userId={user.id}
          email={user.email}
        />
      )}
    </>
  );
};

const defaultValues = { email: '', role: Role.ADMIN };

export default function UsersComponent(): JSX.Element {
  const { t } = useTranslation(['users', 'user_profile', 'settings_page']);
  const queryClient = useQueryClient();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { handleSubmit, control, reset } = useForm<Omit<Invitation, 'id'>>({ defaultValues });
  const { data: invitations } = useQuery<Invitation[]>({ queryKey: ['invitations'] });
  const { data: users } = useQuery<User[]>({ queryKey: ['users'] });
  // Mirror the backend gates on /delete_implementation:
  //  1. @check_scenario_permission(["orgadmin", "quickchat"]): role gate
  //     surfaced via `permissions.delete_ai_agent`.
  //  2. can_remove_implementation: tier gate — `can_delete` is True only on
  //     TRIAL (see payments/feature_bundles/feature_utils.py:50). Custom
  //     accounts are deleted manually by the Quickchat team, so the button
  //     is shown disabled with a "Contact us" tooltip even for the org
  //     owner.
  const { data: aiAssistants } = useQuery<AIAssistantsData>({
    queryKey: [QueryKey.aiAssistants.get],
  });
  const { data: subscriptionData } = useGetSubscription();
  const hasDeleteRole = !!aiAssistants?.current_ai_assistant?.permissions?.delete_ai_agent;
  const isCustomTier = subscriptionData?.current_plan.tier === TierEnum.CUSTOM;
  const canDeleteAiAgent = hasDeleteRole && !isCustomTier;
  // Tooltip precedence: role-based block applies regardless of tier (admin
  // sees "your role" even on CUSTOM). For delete-capable roles on CUSTOM,
  // surface the contact-us message instead.
  const deleteDisabledTooltip = !hasDeleteRole
    ? t('settings_page:tooltips.role_disabled')
    : isCustomTier
      ? t('settings_page:tooltips.delete_custom_tier_contact')
      : null;

  const sendInviteMutation = useMutation<Invitation, unknown, Omit<Invitation, 'id'>>({
    mutationFn: async (data) =>
      await httpClient.post('/v1/api/scenario_users/invites', data).then(({ data }) => data),
    onSuccess: (data) => {
      reset(defaultValues);
      queryClient.setQueryData<Invitation[]>(['invitations'], (oldData = []) => [...oldData, data]);
    },
  });

  const { isActivated, getConfig, isFetched: isSubscriptionFetched } = useSubscription();
  const collaborators = getConfig('collaborators') as number | null;
  const memberCount =
    (users ?? []).filter(({ role }) => role !== Role.OWNER).length + (invitations ?? []).length;
  const totalMembers = users && invitations ? users.length + invitations.length : null;
  // `collaborators` is `null` for the custom tier (unlimited) and `undefined`
  // while the subscription query is loading. Only compute slot/seat numbers
  // when we have a real numeric cap AND users/invitations have loaded — the
  // JSX gates on these being non-null to hide the badge during loading.
  const collaboratorsLoaded = typeof collaborators === 'number';
  const availableSlots =
    collaboratorsLoaded && users && invitations ? Math.max(0, collaborators - memberCount) : null;
  // +1 for the owner, who is excluded from `memberCount` above.
  const totalSeats = collaboratorsLoaded ? collaborators + 1 : null;
  const canAddUser = availableSlots === null || availableSlots > 0;

  const onSubmit = handleSubmit(async (data) => await sendInviteMutation.mutateAsync(data));

  const hasMembers = (invitations?.length ?? 0) > 0 || (users?.length ?? 0) > 0;

  return (
    <div className="space-y-6">
      {/* Invite Section */}
      <Card>
        <CardHeader className="pb-6">
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div>
              <CardTitle>
                {t('title')}
                {totalMembers !== null && ` (${totalMembers})`}
              </CardTitle>
              <CardDescription className="mt-1.5">{t('invite.description')}</CardDescription>
            </div>
            {availableSlots !== null && (
              <Badge variant={canAddUser ? 'secondary' : 'error'} className="font-normal shrink-0">
                {t('invite.available_collaborators', {
                  count: availableSlots,
                  total: totalSeats,
                })}
              </Badge>
            )}
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={onSubmit} className="mb-8 flex flex-col gap-3 sm:flex-row">
            <Controller
              control={control}
              name="email"
              rules={{ required: true }}
              render={({ field, fieldState: { invalid } }) => (
                <div className="w-full sm:flex-1">
                  <Label htmlFor="invite-email" className="sr-only">
                    {t('email.label')}
                  </Label>
                  <Input
                    id="invite-email"
                    type="email"
                    placeholder="colleague@company.com"
                    {...field}
                    error={invalid}
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="role"
              render={({ field }) => {
                const userRolesEnabled = !isSubscriptionFetched || isActivated('user_roles');
                const select = (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={!userRolesEnabled}
                  >
                    <SelectTrigger className="h-10 w-full sm:w-72">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={Role.ADMIN}>
                        <span className="flex items-center gap-2">{t('role.options.admin')}</span>
                      </SelectItem>
                      <SelectItem value={Role.EDITOR}>
                        <span className="flex items-center gap-2">{t('role.options.editor')}</span>
                      </SelectItem>
                      <SelectItem value={Role.SUPPORT}>
                        <span className="flex items-center gap-2">{t('role.options.support')}</span>
                      </SelectItem>
                      <SelectItem value={Role.VIEWER}>
                        <span className="flex items-center gap-2">{t('role.options.viewer')}</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                );
                if (userRolesEnabled) return select;
                // Disabled trigger has `disabled:pointer-events-none`, so wrap
                // in a span to capture hover for the tooltip. Inviter is
                // pinned to ADMIN — the only role available on tiers without
                // the user_roles feature.
                return (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-block w-full sm:w-auto">{select}</span>
                      </TooltipTrigger>
                      <TooltipContent>{t('role.upgrade_required')}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              }}
            />

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* span wrapper captures hover events even when the button
                      is disabled (Button sets disabled:pointer-events-none) */}
                  <span className="inline-block w-full sm:w-auto sm:shrink-0">
                    <Button
                      type="submit"
                      variant="dark"
                      size="md"
                      disabled={sendInviteMutation.isPending || !canAddUser}
                      className="h-10 w-full min-w-24 border-0 px-4 sm:w-auto sm:shrink-0"
                    >
                      {sendInviteMutation.isPending ? (
                        <Spinner className="h-4 w-4 text-white" />
                      ) : (
                        <>
                          <Send className="h-5 w-5" />
                          <span className="text-sm font-semibold">{t('invite.button')}</span>
                        </>
                      )}
                    </Button>
                  </span>
                </TooltipTrigger>
                {!canAddUser && !sendInviteMutation.isPending && (
                  <TooltipContent>{t('invite.seat_limit_reached')}</TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </form>

          {/* Members List */}
          {hasMembers && (
            <div>
              <div className="space-y-3">
                {(invitations ?? []).map((invitation) => (
                  <InvitationItem key={invitation.id} invitation={invitation} />
                ))}
                {(users ?? []).map((user) => (
                  <UserItem key={user.id} user={user} />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Danger Zone — always visible. The Delete trigger is enabled only
          for orgadmin / quickchat on TRIAL tier. Disabled with a
          role-specific tooltip otherwise (admin/editor/etc.) or with a
          contact-us tooltip on CUSTOM tier. */}
      <Card>
        <CardHeader className="pb-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>{t('danger_zone.title')}</CardTitle>
              <CardDescription className="mt-1.5">{t('danger_zone.description')}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                {/* span wrapper captures hover events even when the button
                    is disabled (Button sets disabled:pointer-events-none) */}
                <span className="inline-block">
                  <Button
                    variant="danger"
                    disabled={!canDeleteAiAgent}
                    onClick={() => setIsDeleteModalOpen(true)}
                    data-testid="delete-ai-agent-trigger"
                  >
                    <Trash2 className="size-4" />
                    {t('user_profile:delete.button.action')}
                  </Button>
                </span>
              </TooltipTrigger>
              {deleteDisabledTooltip && <TooltipContent>{deleteDisabledTooltip}</TooltipContent>}
            </Tooltip>
          </TooltipProvider>
          <DeleteAIModal open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen} />
        </CardContent>
      </Card>
    </div>
  );
}
